import { useState, useRef, useCallback, useEffect } from "react";

type SpeechInputOptions = {
  onResult: (transcript: string) => void;
  lang?: string;
};

export type SpeechInputHandle = {
  listening: boolean;
  supported: boolean;
  toggle: () => void;
  stop: () => void;
};

type SRResult = { isFinal: boolean; 0: { transcript: string } };

type SRInstance = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onstart: (() => void) | null;
  onresult: ((event: Event) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
};

export function useSpeechInput({ onResult, lang = "en-AU" }: SpeechInputOptions): SpeechInputHandle {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SRInstance | null>(null);
  const onResultRef = useRef(onResult);

  useEffect(() => {
    onResultRef.current = onResult;
  });

  const supported =
    typeof window !== "undefined" &&
    !!((window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor: (new () => SRInstance) | undefined =
      (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!Ctor) return;

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event: Event) => {
      const e = event as unknown as {
        results: ArrayLike<SRResult>;
        resultIndex: number;
      };
      const newText = Array.from(e.results)
        .slice(e.resultIndex)
        .filter((r) => r.isFinal)
        .map((r) => r[0].transcript.trim())
        .filter(Boolean)
        .join(" ");
      if (newText) onResultRef.current(newText);
    };

    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  }, [lang]);

  const toggle = useCallback(() => {
    if (listening) {
      stop();
    } else {
      start();
    }
  }, [listening, start, stop]);

  return { listening, supported, toggle, stop };
}
